import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Tokens — virtual currency derived from the user's match results.
 * 10 league points = 1 token (3 pts win, 1 pt draw). Persisted server-side.
 *
 * Iter 40: now also exposes owned skins + active skin so the V2 Store, Profile
 * MySkins panel, and SocialCard skin overlay can read/write a single source of
 * truth. `redeemSkin` and `applySkin` mutate server state then refresh.
 */
const TokenContext = createContext(null);

const POLL_MS = 30000;

export function TokenProvider({ children }) {
  const { user, refreshMe } = useAuth();
  const [state, setState] = useState({
    balance: 0, earned: 0, spent: 0,
    total_points: 0, progress_in_block: 0, progress_pct: 0,
    next_token_at: 10, points_per_token: 10,
    history: [],
    loading: true,
  });
  const [skinState, setSkinState] = useState({ owned_skins: [], active_skin: null, loadingSkins: true });
  const [reward, setReward] = useState(null);
  const lastSeenRef = useRef(null);

  const storageKey = user?.id ? `eplay:tokens:lastSeen:${user.id}` : null;

  const load = useCallback(async ({ silent = false, fromAction = null } = {}) => {
    if (!user?.id) return;
    try {
      if (!silent) setState((s) => ({ ...s, loading: true }));
      const { data } = await api.get("/me/tokens?limit=50");
      setState({ ...data, loading: false });
      // Compare against last seen snapshot to detect token earned events.
      if (storageKey) {
        const prevRaw = localStorage.getItem(storageKey);
        const prev = prevRaw ? JSON.parse(prevRaw) : null;
        if (prev && data.balance > prev.balance) {
          // Token gained — compose reward payload from the latest history entry.
          const latest = (data.history || [])[0];
          const fromLatest = latest && (latest.tokens_after > prev.balance);
          setReward({
            points: latest?.points ?? 0,
            opponent: latest?.opponent_username,
            outcome: latest?.outcome || (latest?.type === "simulated" ? "simulated" : "win"),
            balanceBefore: prev.balance,
            balanceAfter: data.balance,
            tokensGained: data.balance - prev.balance,
            score: latest?.score,
            simulated: latest?.type === "simulated" || fromAction === "simulate",
            // If we couldn't tie back a single match, fall back to "match" generic
            isFromMatch: !!fromLatest && latest?.type === "match",
          });
        }
        localStorage.setItem(storageKey, JSON.stringify({ balance: data.balance, ts: Date.now() }));
        lastSeenRef.current = data.balance;
      }
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [user?.id, storageKey]);

  // Initial load + light polling so cross-device match confirmations fire the
  // overlay even when the user is parked on the V2 home tab.
  useEffect(() => {
    if (!user?.id) return;
    load();
    const t = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(t);
  }, [user?.id, load]);

  const simulateWin = useCallback(async () => {
    if (!user?.id) return null;
    try {
      const { data } = await api.post("/me/tokens/simulate-win", {});
      // Force load to compare against snapshot and queue the overlay.
      await load({ silent: true, fromAction: "simulate" });
      return data;
    } catch (e) {
      return null;
    }
  }, [user?.id, load]);

  const loadSkins = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get("/skins/catalog");
      setSkinState({
        owned_skins: data.owned_skins || [],
        active_skin: data.active_skin || null,
        loadingSkins: false,
      });
      return data;
    } catch {
      setSkinState((s) => ({ ...s, loadingSkins: false }));
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    loadSkins();
  }, [user?.id, loadSkins]);

  const redeemSkin = useCallback(async (skinId) => {
    if (!user?.id) return null;
    try {
      const { data } = await api.post(`/skins/${skinId}/redeem`);
      await load({ silent: true });        // refresh balance
      await loadSkins();                    // refresh ownership
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Redemption failed" };
    }
  }, [user?.id, load, loadSkins]);

  const applySkin = useCallback(async (skinId) => {
    if (!user?.id) return null;
    try {
      const { data } = await api.post("/skins/apply", { skin_id: skinId });
      setSkinState({
        owned_skins: data.owned_skins || [],
        active_skin: data.active_skin || null,
        loadingSkins: false,
      });
      // Push the change up to AuthContext so other components (e.g., V1
      // SocialCard, V2 Profile hero) re-render with the new skin.
      try { await refreshMe?.(); } catch { /* noop */ }
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Apply failed" };
    }
  }, [user?.id, refreshMe]);

  const dismissReward = useCallback(() => setReward(null), []);

  return (
    <TokenContext.Provider value={{
      ...state,
      ...skinState,
      reload: load,
      simulateWin,
      redeemSkin,
      applySkin,
      reloadSkins: loadSkins,
      reward,
      dismissReward,
    }}>
      {children}
    </TokenContext.Provider>
  );
}

export function useTokens() {
  const ctx = useContext(TokenContext);
  if (!ctx) throw new Error("useTokens must be used inside TokenProvider");
  return ctx;
}
